import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchWithAuth } from "../utils/fetchWithAuth";

export default function DocumentCenter() {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  
  // Action type toggle
  const [actionType, setActionType] = useState("existing"); // "existing" | "new"

  // Create new document fields
  const [docType, setDocType] = useState("Lesson Plan");
  const [isGenerating, setIsGenerating] = useState(false);

  // Unified Form state
  const [docUrl, setDocUrl] = useState("");
  const [useCase, setUseCase] = useState("");
  const [tags, setTags] = useState("");
  const [subject, setSubject] = useState("");
  const [cohort, setCohort] = useState("Beginner");
  const [isTimelyRanged, setIsTimelyRanged] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  const [isSummarising, setIsSummarising] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVectorizing, setIsVectorizing] = useState(false);
  const [vectorizingDocId, setVectorizingDocId] = useState(null);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser) {
      navigate("/login");
    } else {
      setUser(storedUser);
    }
  }, [navigate]);

  const handleGenerateDocument = async () => {
    setIsGenerating(true);
    try {
      const response = await fetchWithAuth("/api/doc-center/create", {
        method: "POST",
        body: JSON.stringify({ category: docType }),
      });
      const data = await response.json();
      if (response.ok) {
        setDocUrl(data.url);
      } else {
        alert(data.error || data.detail || "Failed to generate document.");
      }
    } catch (error) {
      console.error(error);
      alert("Network error.");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSummarise = async () => {
    if (!docUrl) return;
    setIsSummarising(true);
    try {
      const response = await fetchWithAuth("/api/doc-center/summarise", {
        method: "POST",
        body: JSON.stringify({ url: docUrl }),
      });
      const data = await response.json();
      if (response.ok) {
        setUseCase(data.use_case || "");
        setSubject(data.subject || "");
        setCohort(data.cohort || "Beginner");
        setTags(data.tags || "");
        setIsTimelyRanged(!!data.is_timely_ranged);
        setStartDate(data.start_date || "");
        setEndDate(data.end_date || "");
      } else {
        alert(data.error || data.detail || "Summarization failed.");
      }
    } catch (error) {
      console.error(error);
      alert("Network error.");
    } finally {
      setIsSummarising(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!docUrl || !useCase) {
        alert("Link and Use Case are mandatory.");
        return;
    }
    
    setIsSubmitting(true);
    try {
      const response = await fetchWithAuth("/api/doc-center/register", {
        method: "POST",
        body: JSON.stringify({
          url: docUrl,
          category: actionType === "new" ? docType : "Legacy Sync",
          use_case: useCase,
          tags: tags,
          subject: subject,
          cohort: cohort,
          is_timely_ranged: isTimelyRanged,
          start_date: startDate,
          end_date: endDate
        }),
      });
      const data = await response.json();
      if (response.ok) {
        setVectorizingDocId(data.doc_id);
        setIsVectorizing(true);
        
        // Start polling for vectorization status
        pollVectorizationStatus(data.doc_id);
        
        // Reset form immediately so user doesn't double-submit
        setDocUrl("");
        setUseCase("");
        setTags("");
        setSubject("");
        setIsTimelyRanged(false);
        setStartDate("");
        setEndDate("");
      } else {
        alert(data.error || data.detail || "Registration failed.");
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error(error);
      alert("Network error.");
      setIsSubmitting(false);
    }
  };

  const pollVectorizationStatus = async (docId) => {
    const maxRetries = 60; // 2 minutes with 2s interval
    let retries = 0;
    
    const interval = setInterval(async () => {
        try {
            const response = await fetchWithAuth(`/api/doc-center/status/${docId}`);
            if (response.ok) {
                const data = await response.json();
                if (data.status === "vectorized") {
                    clearInterval(interval);
                    setIsVectorizing(false);
                    setVectorizingDocId(null);
                    alert("Success! Document is now fully vectorized and available in RAG.");
                    setIsSubmitting(false);
                } else if (data.status === "failed") {
                    clearInterval(interval);
                    setIsVectorizing(false);
                    setVectorizingDocId(null);
                    alert("Vectorization failed. Please check backend logs.");
                    setIsSubmitting(false);
                }
            }
        } catch (e) {
            console.error("Polling error:", e);
        }
        
        retries++;
        if (retries >= maxRetries) {
            clearInterval(interval);
            setIsVectorizing(false);
            setVectorizingDocId(null);
            alert("Vectorization is taking longer than expected. It will continue in the background.");
            setIsSubmitting(false);
        }
    }, 2000);
  };

  if (!user) return null;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-slate-50 text-slate-600">
      <div className="flex shrink-0 flex-col gap-2 border-b border-slate-200 bg-white/70 px-6 py-8 backdrop-blur-sm md:px-10">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-purple-600">Document Center</p>
          <h1 className="mt-1 text-3xl font-semibold text-slate-800">Review & Registration</h1>
          <p className="mt-2 text-sm text-slate-500">
            {isVectorizing 
              ? "Your document is being semantically chunked and vectorized..." 
              : "Validate, summarize, and register your documents for the DC Validated RAG pipeline."}
          </p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 md:p-10">
        <div className="mx-auto max-w-4xl space-y-8">
          
          {isVectorizing && (
            <div className="rounded-2xl border border-purple-200 bg-purple-50 p-8 text-center shadow-sm animate-pulse">
               <div className="mb-4 flex justify-center">
                  <svg className="h-12 w-12 animate-spin text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
               </div>
               <h3 className="text-xl font-bold text-purple-800">Vectorizing Document...</h3>
               <p className="mt-2 text-purple-600">We are currently splitting your document into semantic chunks and indexing them for perfect retrieval.</p>
               <p className="mt-4 text-xs font-mono text-purple-400">ID: {vectorizingDocId}</p>
            </div>
          )}

          {!isVectorizing && (
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Step 1: Document Source</h2>
            <div className="mb-6 flex rounded-xl bg-slate-200/50 p-1">
              <button
                onClick={() => setActionType("existing")}
                className={`flex-1 rounded-lg py-2.5 text-sm font-semibold transition-all duration-300 ${
                  actionType === "existing" ? "bg-white text-purple-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                Use Existing Link
              </button>
              <button
                onClick={() => setActionType("new")}
                className={`flex-1 rounded-lg py-2.5 text-sm font-semibold transition-all duration-300 ${
                  actionType === "new" ? "bg-white text-purple-600 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                Create New Form Template
              </button>
            </div>

            {actionType === "new" && (
              <div className="mb-6 flex items-end gap-4 animate-in fade-in slide-in-from-top-2">
                <div className="flex-1 space-y-2">
                  <label className="text-sm font-medium text-slate-700">Document Type</label>
                  <select
                    value={docType}
                    onChange={(e) => setDocType(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                  >
                    <option value="Lesson Plan">Lesson Plan</option>
                    <option value="Question Bank">Question Bank</option>
                    <option value="Syllabus">Syllabus</option>
                  </select>
                </div>
                <button
                  type="button"
                  disabled={isGenerating}
                  onClick={handleGenerateDocument}
                  className="rounded-xl bg-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-purple-700 disabled:opacity-50"
                >
                  {isGenerating ? "Generating..." : "Generate & Link"}
                </button>
              </div>
            )}
            
            <form onSubmit={handleRegister} className="space-y-6">
            <div className="flex items-center justify-between border-b pb-4">
               <h2 className="text-lg font-semibold text-slate-800">Step 2: Manual Review & Registration</h2>
               <button
                  type="button"
                  disabled={isSummarising || !docUrl}
                  onClick={handleSummarise}
                  className="rounded-lg bg-emerald-100 text-emerald-700 px-4 py-2 text-sm font-semibold transition-all hover:bg-emerald-200 disabled:opacity-50 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
                  {isSummarising ? "Analyzing..." : "Summarise document"}
                </button>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Link to the document <span className="text-rose-500">*</span></label>
                <input
                  type="url"
                  required
                  placeholder="https://docs.google.com/document/d/..."
                  value={docUrl}
                  onChange={(e) => setDocUrl(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm focus:border-purple-500 focus:outline-none"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Use case of the document <span className="text-rose-500">*</span></label>
                <textarea
                  required
                  rows="3"
                  placeholder="Describe the pedagogical intent or general use case..."
                  value={useCase}
                  onChange={(e) => setUseCase(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm focus:border-purple-500 focus:outline-none"
                />
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Tags (comma separated)</label>
                  <input
                    type="text"
                    placeholder="e.g. algebra, final exam, 2026"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm focus:border-purple-500 focus:outline-none"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Subject</label>
                  <input
                    type="text"
                    placeholder="e.g. Mathematics"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm focus:border-purple-500 focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Cohort</label>
                <input
                  type="text"
                  placeholder="e.g. Advanced, 10th Grade"
                  value={cohort}
                  onChange={(e) => setCohort(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm focus:border-purple-500 focus:outline-none"
                />
              </div>

              <div className="flex items-center gap-3 pt-2">
                <input
                  type="checkbox"
                  id="rangedToggle"
                  checked={isTimelyRanged}
                  onChange={(e) => setIsTimelyRanged(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-600"
                />
                <label htmlFor="rangedToggle" className="text-sm font-medium text-slate-700">Is Timely Ranged</label>
              </div>

              {isTimelyRanged && (
                <div className="grid gap-6 md:grid-cols-2 animate-in slide-in-from-top-2">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Start Date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">End Date</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm focus:border-purple-500 focus:outline-none"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="mt-8 border-t pt-6 flex justify-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-8 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-slate-800 focus:outline-none disabled:opacity-50"
              >
                {isSubmitting ? "Registering..." : "Submit to Pipeline"}
              </button>
            </div>
          </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
